import classnames from 'classnames';
import equal from 'fast-deep-equal/react';
import find from 'lodash/find';
import get from 'lodash/get';
import { memo, useState } from 'react';

import { getShipStatData, setSortField, toggleFilter } from '~/Actions/ActionApp';
import Autocomplete from '~/Components/Autocomplete/Autocomplete';
import DetailInfo from '~/Components/DetailInfo/DetailInfo';
import Filter from '~/Components/Filter/Filter';
import Expand from '~/Components/Table/Expand';
import Table, { BasicColumn } from '~/Components/Table/Table';
import { playTabClickSound } from '~/helpers/audioApi';
import { RootState, useAppDispatch, useAppSelector } from '~/store';
import { appActions } from '~/store/appSlice';
import { isInGame } from '~/utils';
import { formatShipLevel, thousands } from '~/utils/formatting';
import { t } from '~/utils/localization';

import styles from './ShipsTable.scss';
import WoWSEntity from '@wg/wows-entities/wrappers/react/WoWSEntity';
import { MASTERY, PresentationStyles, items } from '@wg/wows-entities/const';

export interface TableColumn extends BasicColumn {
  selector: CellSelector;
}

export type TableColumns = Array<TableColumn>;

const stateSelector = (state: RootState) => {
  return {
    vehiclesMap: state.app.vehiclesMap,
    vehicleTypesMap: state.app.vehicleTypesMap,
    sortField: state.app.sortField,
    sortDirection: state.app.sortDirection,
    shipStatistics: state.app.shipStatistics,
    statisticsData: state.app.statisticsData,
    battleType: state.app.battleType,
    seasonPickerVisible: state.app.seasonPickerVisible,
    seasonNumber: state.app.seasonNumber,
    shipFilterByTitleValue: state.app.shipFilterByTitleValue,
  };
};

export interface ContainFieldsForFilter {
  [nation: string]: Set<string | number | boolean>;
  type: Set<string>;
  level: Set<number>;
  shipType: Set<string>;
}

export interface ShipsTable_Props {
  sortedShips: Array<TableShipStat>;
  allShips: Array<TableShipStat>;
}

const ShipsTable = (props: ShipsTable_Props) => {
  const columns: TableColumns = [
    {
      selector: 'nation',
    },
    {
      selector: 'class',
    },
    {
      selector: 'level',
    },
    {
      selector: 'name',
    },
    {
      selector: 'battles_count',
    },
    {
      selector: 'wins',
    },
    {
      selector: 'premium_exp',
    },
  ];

  const dispatch = useAppDispatch();
  const {
    vehiclesMap,
    vehicleTypesMap,
    sortField,
    sortDirection,
    shipStatistics,
    statisticsData,
    battleType,
    seasonPickerVisible,
    seasonNumber,
    shipFilterByTitleValue,
  } = useAppSelector(stateSelector, equal);

  if (battleType === 'pvp') {
    columns.push({ selector: 'mastery'});
  }

  const [expandedId, setExpandedId] = useState<string | undefined>();
  const { sortedShips, allShips } = props;

  const [filteredShips, setFilteredShips] = useState<Array<TableShipStat>>([]);

  const containFieldsForFilter: ContainFieldsForFilter = {
    nation: new Set(),
    type: new Set(),
    level: new Set(),
    shipType: new Set(),
  };

  allShips.forEach((ship: TableShipStat) => {
    const shipInfo = vehiclesMap[ship.id];
    containFieldsForFilter.nation.add(shipInfo.nation.name);
    containFieldsForFilter.type.add(shipInfo.type.name);
    containFieldsForFilter.level.add(shipInfo.level);
    if (shipInfo.isSpecial) {
      containFieldsForFilter.shipType.add('isSpecial');
    } else if (shipInfo.isPremium) {
      containFieldsForFilter.shipType.add('isPremium');
    }
  });

  const onRowClick = (item: TableShipStat) => {
    if (item.id === expandedId) {
      setExpandedId(undefined);
    } else {
      const open = () => {
        dispatch(
          getShipStatData(item.id, () => {
            setExpandedId(item.id);
          }),
        );
      };
      if (expandedId) {
        setExpandedId(undefined);
        setTimeout(open, 300);
      } else {
        open();
      }
    }
  };

  const renderCell = (column: TableColumn, index: number) => {
    const item = sortedShips[index];
    const shipInfo = vehiclesMap[item.id];
    if (!shipInfo) {
      return '';
    }
    const vehicleType = vehicleTypesMap[shipInfo.type.name];
    const textClassName = classnames(styles.text, {
      [styles.yellow]: shipInfo.isPremium || shipInfo.isSpecial,
      [styles.boldCaps]: column.selector === 'name' || column.selector === 'level',
    });
    const statItem = find(statisticsData, { id: item.id }, undefined);

    let statData = get(statItem, battleType, undefined);
    if (seasonPickerVisible) {
      statData = get(statItem, `seasons.${seasonNumber}.${battleType}`, undefined);
    }

    switch (column.selector) {
      case 'nation': {
        return (
          <div
            className={styles.nationCellIcon}
            style={{
              backgroundImage: `url(${shipInfo.nation.icons.tiny})`,
            }}
          />
        );
      }
      case 'class': {
        let iconUrl = vehicleType.icons.normal;
        if (shipInfo.isSpecial) {
          iconUrl = vehicleType.icons.special;
        }
        if (shipInfo.isPremium) {
          iconUrl = vehicleType.icons.premium;
        }
        return (
          <div
            className={styles.nationIcon}
            style={{
              backgroundImage: `url(${iconUrl})`,
            }}
          />
        );
      }
      case 'level': {
        return <div className={textClassName}>{formatShipLevel(shipInfo.level)}</div>;
      }
      case 'name': {
        return (
          <div className={styles.shipCellName}>
            <div
              className={styles.shipCellIcon}
              style={{
                backgroundImage: `url(${shipInfo.icons.small})`,
              }}
            />
            <div className={textClassName}>{shipInfo.titleShort}</div>
          </div>
        );
      }
      case 'battles_count': {
        return <div className={textClassName}>{get(statData, column.selector, 0)}</div>;
      }
      case 'wins': {
        return <div className={textClassName}>{get(statData, column.selector, 0)}</div>;
      }
      case 'premium_exp': {
        return <div className={textClassName}>{thousands(get(statData, column.selector, 0))}</div>;
      }
      case 'mastery': {
        return (
          <div className={styles.masteryComponent}>
            {statItem.mastery_sign && statItem.mastery_sign !== MASTERY.NoSign && (
              <WoWSEntity
                key={`mastery-${statItem.mastery_sign}_${shipInfo.id}_${index}`}
                type={items.MASTERY}
                id={statItem.mastery_sign}
                presentation={{
                  style: PresentationStyles.INLINE,
                  renderWithoutFade: true,
                  withTooltip: true,
                }}
              />
            )}
          </div>
        )
      }
      default: {
        return <div>{column.selector}</div>;
      }
    }
  };

  const renderHeaderCell = (column: TableColumn) => {
    switch (column.selector) {
      case 'premium_exp': {
        return (
          <div className={styles.xpCount}>
            <span>{t('XP')}</span>
          </div>
        );
      }
      case 'wins': {
        return (
          <div className={styles.winsCount}>
            <span>{t('Victories:duplicated0')}</span>
          </div>
        );
      }
      case 'battles_count': {
        return (
          <div className={styles.battlesCount}>
            <span>{t('Battles:duplicated0')}</span>
          </div>
        );
      }
      case 'name': {
        return <div className={styles.shipName}>{t('Warship name')}</div>;
      }
      case 'level': {
        return <span>{`I-X`}</span>;
      }
      case 'class': {
        return <div className={styles.classIcon} />;
      }
      case 'nation': {
        return <div className={styles.nationIcon} />;
      }
      case 'mastery': {
        return <div className={styles.efficiency}>{t('Efficiency')}</div>;
      }
      default: {
        return <div>{column.selector}</div>;
      }
    }
  };

  const renderAfterRow = (index: number) => {
    const id = sortedShips[index].id;
    const isExpanded = expandedId === id;
    const item = sortedShips[index];
    const statItem = find(statisticsData, { id: item.id }, undefined);

    return (
      <Expand isExpanded={isExpanded}>
        <DetailInfo statistics={shipStatistics} totalStats={statItem} battleType={battleType} />
      </Expand>
    );
  };

  const onHeaderCellClick = (column: TableColumn) => {
    playTabClickSound();
    dispatch(setSortField(column.selector));
  };

  const onTableHeaderStickyStateChange = (isSticky: boolean) => {
    dispatch(appActions.setShipTableIsSticky({ isSticky }));
  };

  const onShipInputRenderItem = (index: number) => {
    const item = filteredShips[index];
    return <div>{vehiclesMap[item.id].title}</div>;
  };

  const onShipInputSelect = (index: number) => {
    const item = filteredShips[index];
    onShipNameChange(vehiclesMap[item.id].title);
  };

  const onShipNameChange = (value: string) => {
    dispatch(appActions.setShipFilterByTitleValue({ shipFilterByTitleValue: value }));
    dispatch(toggleFilter('title', value, true));
    setFilteredShips(filterShips(value));
  };

  const filterShips = (title: string) => {
    if (title === null || title.match(/^ *$/) !== null) {
      return [];
    }
    title = title.toLowerCase();
    return allShips.filter((item) => {
      const vehicleTitle = vehiclesMap[item.id].title.toLowerCase();
      return vehicleTitle.includes(title);
    });
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.title}>{t('Warships')}</div>
      <div className={styles.filters}>
        <Filter containFieldsForFilter={containFieldsForFilter} />
        <Autocomplete
          placeholder={t('Warship name')}
          value={shipFilterByTitleValue}
          suggestions={filteredShips}
          onChange={onShipNameChange}
          renderItem={onShipInputRenderItem}
          onSelect={onShipInputSelect}
        />
      </div>
      <Table
        items={sortedShips}
        columns={columns}
        onRowClick={onRowClick}
        onHeaderCellClick={onHeaderCellClick}
        renderCell={renderCell}
        renderHeaderCell={renderHeaderCell}
        renderAfter={renderAfterRow}
        sort={sortField}
        sortDirection={sortDirection}
        onHeaderStickyStateChange={onTableHeaderStickyStateChange}
        topOffset={isInGame ? 107 : 144}
      />
    </div>
  );
};

export default memo(ShipsTable, equal);
